/**
 * A calculation handed to Tonnie from a portal's calculator (D7), on its way
 * into the account. Sign-up receives its one-time token in the address
 * (`/signup?handoff=…`, the link the portal's calculator opened); it is kept
 * in this tab until the InOut page adopts it — through sign-up or sign-in, a
 * code by email, a social sign-in — and leaves the address bar as soon as it
 * is read (it opens the calculation for 72 hours).
 *
 * A link that holds nothing to adopt — used, past its 72 hours, or not a
 * token at all — is kept as spent: the person still came from InOut's
 * calculator, so once there is an account they land on InOut, whose start is
 * the calculator (`cameFromCalculator`), and the InOut page lets the mark go.
 *
 * sessionStorage: this tab only. Only the token is kept — the calculation is
 * Tonnie's, for 72 hours, until the account adopts it. Every read is
 * validated: anything that is not a token is as good as nothing.
 */
import { inoutHandoffTokenSchema } from '@tonnie/schemas';

const KEY = 'tonnie_inout_handoff';
const PARAM = 'handoff';
/** What the tab keeps for a link that holds nothing to adopt. */
const SPENT = 'spent';

export function keepHandoff(token: string): void {
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    // Storage refused (private mode, quota): sign-up still says it back; InOut's calculator remains.
  }
}

export function pendingHandoff(): string | null {
  try {
    const token = inoutHandoffTokenSchema.safeParse(sessionStorage.getItem(KEY));
    return token.success ? token.data : null;
  } catch {
    return null;
  }
}

/** The link holds nothing to adopt: kept as spent, so the account still lands on InOut. */
export function markHandoffSpent(): void {
  try {
    sessionStorage.setItem(KEY, SPENT);
  } catch {
    // Storage refused: the account lands Home; InOut's calculator is there.
  }
}

/** Whether this tab came from InOut's calculator — a token to adopt, or one spent. */
export function cameFromCalculator(): boolean {
  if (pendingHandoff() !== null) return true;
  try {
    return sessionStorage.getItem(KEY) === SPENT;
  } catch {
    return false;
  }
}

export function dropHandoff(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to drop.
  }
}

/** Whether the address carries a hand-off — a portal's calculator opened this page. */
export function handoffInAddress(): boolean {
  return new URL(window.location.href).searchParams.has(PARAM);
}

/**
 * The token in the address, if there is one: kept for the tab, and taken out
 * of the address (and so out of the history and of any referrer) at once. A
 * link whose token is not one is kept as spent.
 */
export function takeHandoffFromAddress(): string | null {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(PARAM);
  if (raw === null) return null;
  url.searchParams.delete(PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  const token = inoutHandoffTokenSchema.safeParse(raw);
  if (!token.success) {
    markHandoffSpent();
    return null;
  }
  keepHandoff(token.data);
  return token.data;
}
